import { ClientFactory } from "@/main/common/ClientFactory";
import type { MediaAttentionRequest } from "@/main/common/DiscussionViewTypes";

/**
 * The per-page-instance ledger behind the media-attention beacon
 * (`designs/01-now/2026-08-01-media-consumption-signal-capture-primitives-design.md` §4 P1-P4).
 *
 * <p><b>One session, one send.</b> Round-5 decision A5 settled ONE `recordMediaAttention` upsert per
 * discussion-page instance, at hide/unload. Two emitters feed it: `MediaAttentionTracker` (P1 attentive
 * ms, P2 items seen, P4 first attention), which OWNS the session and its lifecycle, and the media
 * opens of P3 (`useBodyMediaLightbox`, `DiscussionCardMediaCarousel`), which reach it only through
 * {@link recordMediaOpen}. Neither open point posts anything itself: a second beacon would be a
 * second upsert with a partial view of the same page instance.
 *
 * <p><b>Keyed by discussion, never "whatever is active".</b> The discussion page also renders related
 * discussion cards and the thoughts' own bodies, each with a lightbox or a gated embed of its own. An
 * open on one of those names a DIFFERENT discussion, so {@link recordMediaOpen} takes the id the
 * opener knows and credits the session only when it is the page instance for that same post. An open
 * with no matching session (the feed, a series page) is dropped: cards have no beacon (D11).
 *
 * <p><b>The clip play signal rides the same ledger (round-6 `2c`, D10 / A10).</b> `plays` (a first-party
 * clip started playing) and `watchThroughs` (playback reached 75 % of its duration) are two more clamped
 * counters beside `opens`, credited by the player through {@link recordMediaPlay} and
 * {@link recordMediaWatchThrough} under exactly the attribution rule above. They are NOT a third open:
 * folding either into `opens` would make the autoplay question (design §12) unanswerable.
 *
 * <p>Everything here is a raw observation. Nothing is normalised client-side (A6); the server clamps
 * again and owns every threshold.
 */

/** P3 clamp, the same value `MediaAttentionService` applies server-side (design §4 P3). */
export const MEDIA_OPENS_CLAMP = 10;
/**
 * The clip counters' clamp (round-6 `2c`, D10 / A10): `plays` and `watchThroughs`, the same ceiling as
 * opens and the same number `DiscussionMediaAttentionRepository.MAX_PLAYS` applies server-side.
 */
export const MEDIA_PLAYS_CLAMP = MEDIA_OPENS_CLAMP;

let activeSession: MediaAttentionSession | null = null;

export class MediaAttentionSession {
    public readonly discussionPublicId: string;

    private attentiveMs = 0;
    private itemsSeen = 0;
    private opens = 0;
    private plays = 0;
    private watchThroughs = 0;
    private firstAttentionMs: number | null = null;

    /** The last payload put on the wire, so an unchanged hide does not repeat it. */
    private lastSentKey: string | null = null;
    private ended = false;

    private constructor(discussionPublicId: string) {
        this.discussionPublicId = discussionPublicId;
    }

    /**
     * Opens the session for this page instance and makes it the one {@link recordMediaOpen} credits.
     * A session begun while another is active replaces it without ending it: React StrictMode runs the
     * owning effect twice, and the first instance's teardown must not release the second's registry
     * slot (see {@link end}).
     */
    public static begin(discussionPublicId: string): MediaAttentionSession {
        const session = new MediaAttentionSession(discussionPublicId);
        activeSession = session;
        return session;
    }

    /** P1 + P2 + P4 from the tracker. Every value is a monotone running total; nothing is decremented. */
    public recordAttention(observation: { attentiveMs: number; itemsSeen: number; firstAttentionMs: number | null }): void {
        if (this.ended) {
            return;
        }
        this.attentiveMs = Math.max(this.attentiveMs, Math.round(observation.attentiveMs));
        this.itemsSeen = Math.max(this.itemsSeen, observation.itemsSeen);
        if (this.firstAttentionMs === null && observation.firstAttentionMs !== null) {
            this.firstAttentionMs = Math.max(0, Math.round(observation.firstAttentionMs));
        }
    }

    /** P3. Clamped here so a click loop cannot inflate the wire value either. */
    public recordOpen(): void {
        if (this.ended) {
            return;
        }
        this.opens = Math.min(MEDIA_OPENS_CLAMP, this.opens + 1);
    }

    /** P3 `plays`. Once per player instance is the PLAYER's rule; the clamp here is the ledger's. */
    public recordPlay(): void {
        if (this.ended) {
            return;
        }
        this.plays = Math.min(MEDIA_PLAYS_CLAMP, this.plays + 1);
    }

    /** P3 `watchThroughs`, on the same terms as {@link recordPlay}. */
    public recordWatchThrough(): void {
        if (this.ended) {
            return;
        }
        this.watchThroughs = Math.min(MEDIA_PLAYS_CLAMP, this.watchThroughs + 1);
    }

    /**
     * What this page instance has observed, with every field it observed nothing for OMITTED, so the
     * server's `GREATEST` upsert never has to tell "sent 0" from "did not send" (`MediaAttentionRequest`).
     */
    public toRequest(): MediaAttentionRequest {
        const request: MediaAttentionRequest = {};
        if (this.attentiveMs > 0) {
            request.attentiveMs = this.attentiveMs;
        }
        if (this.itemsSeen > 0) {
            request.itemsSeen = this.itemsSeen;
        }
        if (this.opens > 0) {
            request.opens = this.opens;
        }
        if (this.plays > 0) {
            request.plays = this.plays;
        }
        if (this.watchThroughs > 0) {
            request.watchThroughs = this.watchThroughs;
        }
        if (this.firstAttentionMs !== null) {
            request.firstAttentionMs = this.firstAttentionMs;
        }
        return request;
    }

    /**
     * Sends the current observation if there is one and it differs from the last send.
     *
     * <p>Fires from `visibilitychange`/`pagehide` handlers where nobody can await it: the wrapper
     * resolves on every failure, and the synchronous guard below covers a transport that throws before
     * returning a promise. The page instance is never told the answer; nothing it does depends on it.
     */
    public flush(): boolean {
        const request = this.toRequest();
        if (Object.keys(request).length === 0) {
            return false;
        }
        const key = JSON.stringify(request);
        if (key === this.lastSentKey) {
            return false;
        }
        this.lastSentKey = key;
        try {
            void Promise.resolve(ClientFactory.recordMediaAttention?.(this.discussionPublicId, request)).catch(() => {
                /* a beacon: the reader is looking at media, and nothing here may reach them */
            });
        } catch {
            /* a synchronous throw from the transport is the same non-event */
        }
        return true;
    }

    /** Final flush, then this session stops accepting observations and leaves the registry. */
    public end(): void {
        if (this.ended) {
            return;
        }
        this.flush();
        this.ended = true;
        if (activeSession === this) {
            activeSession = null;
        }
    }
}

/**
 * P3 entry point for the open gestures. Credits the page instance's session when, and only when, it
 * is the one for `discussionPublicId`; returns whether anything was credited so a caller's spec can
 * assert the attribution rather than the counter.
 */
export function recordMediaOpen(discussionPublicId: string | null | undefined): boolean {
    if (!discussionPublicId || !activeSession || activeSession.discussionPublicId !== discussionPublicId) {
        return false;
    }
    activeSession.recordOpen();
    return true;
}

/** P3 `plays` entry point for the clip player, on {@link recordMediaOpen}'s exact attribution terms. */
export function recordMediaPlay(discussionPublicId: string | null | undefined): boolean {
    if (!discussionPublicId || !activeSession || activeSession.discussionPublicId !== discussionPublicId) {
        return false;
    }
    activeSession.recordPlay();
    return true;
}

/** P3 `watchThroughs` entry point for the clip player, on {@link recordMediaOpen}'s exact attribution terms. */
export function recordMediaWatchThrough(discussionPublicId: string | null | undefined): boolean {
    if (!discussionPublicId || !activeSession || activeSession.discussionPublicId !== discussionPublicId) {
        return false;
    }
    activeSession.recordWatchThrough();
    return true;
}

/** Test seam only: the session currently registered for the page instance. */
export function getActiveMediaAttentionSessionForTests(): MediaAttentionSession | null {
    return activeSession;
}
